import { useDarkMode } from '@/contexts/DarkModeContext';
import Alert from 'antd/es/alert';
import Typography from 'antd/es/typography';
import React, { useState } from 'react';
import { styled } from 'styled-components';
import { JsonEditor } from 'ui-components';

import type { JsonTemplateBlockConfig, PipelineBlock } from '@mai/types';

const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.marginXS}px;
`;

const EditorLabel = styled(Typography.Text)`
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.marginXXS}px;
`;

const EditorHelp = styled(Typography.Text)`
  color: ${({ theme }) => theme.colorTextSecondary};
  font-size: ${({ theme }) => theme.fontSizeSM}px;
  margin-bottom: ${({ theme }) => theme.marginXS}px;
`;

interface JsonTemplateBlockEditorProps {
  block: PipelineBlock & { type: 'json_template' };
  onConfigChange: (config: JsonTemplateBlockConfig) => void;
}

export const JsonTemplateBlockEditor: React.FC<
  JsonTemplateBlockEditorProps
> = ({ block, onConfigChange }) => {
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDarkMode] = useDarkMode();

  // Convert the current template to a string for the editor with prettification
  const currentTemplateString = React.useMemo(() => {
    const template = block.config.template;

    if (!template) {
      return '{}';
    }

    try {
      let jsonToStringify;

      if (typeof template === 'string') {
        if ((template as string).trim() === '') {
          return '{}';
        }
        jsonToStringify = JSON.parse(template);
      } else {
        jsonToStringify = template;
      }

      if (
        typeof jsonToStringify === 'object' &&
        Object.keys(jsonToStringify).length === 0
      ) {
        return '{}';
      }

      return JSON.stringify(jsonToStringify, null, 2);
    } catch {
      if (typeof template === 'string') {
        return template;
      }
      return '{}';
    }
  }, [block.config.template]);

  const handleTemplateChange = React.useCallback(
    (value: string) => {
      if (isValid) {
        try {
          const parsedTemplate = JSON.parse(value);
          onConfigChange({
            ...block.config,
            template: parsedTemplate,
          });
        } catch {
          // Invalid JSON - don't update config
        }
      }
    },
    [block.config, onConfigChange, isValid],
  );

  const handleValidation = React.useCallback(
    (valid: boolean, errorMessage?: string) => {
      setIsValid(valid);
      setError(errorMessage || null);
    },
    [],
  );

  return (
    <EditorContainer>
      <EditorLabel>JSON Template</EditorLabel>
      <EditorHelp>
        JSON template structure with Jinja2 variables to render with input data.
        Use <code>{'{{ variable_name }}'}</code> to reference template
        variables.
      </EditorHelp>

      {error && (
        <Alert
          type="error"
          message="Invalid JSON"
          description={error}
          showIcon
        />
      )}

      <JsonEditor
        value={currentTemplateString}
        onChange={handleTemplateChange}
        onValidate={handleValidation}
        height="300px"
        placeholder={`{
  "message": "Hello {{ name }}",
  "data": {
    "items": {{ items | tojson }},
    "count": {{ items | length }}
  }
}`}
        theme={isDarkMode ? 'dark' : 'light'}
      />
    </EditorContainer>
  );
};
